import React, { useState, useEffect } from 'react';
import './App.css';

const App = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    if (email) {
      // Here you would typically send to your backend
      console.log('Email submitted:', email);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('waitlist');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Structured Data for SEO
  useEffect(() => {
    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Fit AI",
      "alternateName": "FitAI",
      "url": "https://fitai.app",
      "logo": "https://fitai.app/logo512.png",
      "description": "AI-Powered Fitness Companion - Transform your fitness journey with intelligent exercise alternatives",
      "foundingDate": "2025",
      "sameAs": [
        "https://twitter.com/FitAI",
        "https://instagram.com/FitAI",
        "https://youtube.com/@FitAI",
        "https://linkedin.com/company/fitai"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@fitai.app",
        "availableLanguage": ["English"]
      }
    };

    // Software Application Schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Fit AI",
      "applicationCategory": "HealthApplication",
      "applicationSubCategory": "Fitness",
      "operatingSystem": ["iOS", "Android"],
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "10000",
        "bestRating": "5",
        "worstRating": "1"
      },
      "description": "Revolutionary AI fitness app. Take a photo of any exercise and get instant personalized alternatives. 1M+ exercises database tailored to your goals.",
      "screenshot": "https://fitai.app/screenshot.jpg",
      "featureList": [
        "AI-Powered Exercise Alternatives",
        "1M+ Exercise Database",
        "Machine Equipment Scanning",
        "Automatic Progress Tracking",
        "Manual Workout Logging",
        "Apple Health Integration",
        "Community Exercise Library",
        "Smart Training Memory"
      ]
    };

    // WebSite Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Fit AI",
      "url": "https://fitai.app",
      "description": "AI-Powered Fitness Companion - Train Smarter, Not Harder",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://fitai.app/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    // Mobile Application Schema
    const mobileAppSchema = {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "name": "Fit AI",
      "operatingSystem": "iOS, Android",
      "applicationCategory": "HealthApplication",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "10000"
      }
    };

    // FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Fit AI?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Fit AI is an AI-powered fitness companion that provides personalized exercise alternatives. Simply take a photo of any exercise and get instant alternatives tailored to your goals, limitations, and available equipment."
          }
        },
        {
          "@type": "Question",
          "name": "How does Fit AI work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Fit AI uses advanced AI to analyze exercises in three simple steps: 1) Snap a photo or search our 1M+ exercise database, 2) AI analyzes your goals and limitations, 3) Get personalized alternatives and track your progress automatically."
          }
        },
        {
          "@type": "Question",
          "name": "Is Fit AI free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Fit AI will offer both free and premium tiers. Join our waitlist to get early access and exclusive launch pricing when we launch on iOS and Android."
          }
        },
        {
          "@type": "Question",
          "name": "What makes Fit AI different from other fitness apps?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Fit AI is the first app to offer AI-powered exercise alternatives via photo analysis. Unlike generic fitness trackers, we provide personalized alternatives based on YOUR body, goals, equipment, and limitations. Plus, our community-powered database includes specialized movements like boxing and calisthenics."
          }
        }
      ]
    };

    // Insert schemas into head
    const schemas = [organizationSchema, softwareSchema, websiteSchema, mobileAppSchema, faqSchema];
    const scriptElements = [];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-${index}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });

    // Cleanup
    return () => {
      scriptElements.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl flex items-center justify-center" role="img" aria-label="Fit AI Logo">
                <span className="text-white font-bold text-xl">F</span>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                Fit AI
              </span>
            </div>
            <button
              onClick={scrollToWaitlist}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              aria-label="Join Waitlist - Get early access to Fit AI"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 overflow-hidden" role="banner">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-6" role="note">
                <span className="text-purple-700 font-semibold text-sm">🚀 AI-Powered Fitness Revolution</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Train Smarter,
                <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent"> Not Harder</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 mb-8 leading-relaxed" role="doc-subtitle">
                Take a photo of any exercise and get instant personalized alternatives tailored to your goals, limitations, and equipment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" role="group" aria-label="Call to action buttons">
                <button
                  onClick={scrollToWaitlist}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-full font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  aria-label="Join Fit AI Waitlist - Get early access to AI-powered fitness"
                  tabIndex={0}
                >
                  Join Waitlist
                </button>
                <button
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-white text-purple-600 rounded-full font-semibold text-lg border-2 border-purple-600 hover:bg-purple-50 transition-all duration-200"
                  aria-label="Learn how Fit AI works - See demo"
                  tabIndex={0}
                >
                  See How It Works
                </button>
              </div>
              <div className="mt-8 flex items-center space-x-6" role="complementary" aria-label="Social proof">
                <div className="flex -space-x-2" role="img" aria-label="User avatars">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-white" aria-hidden="true"></div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-white" aria-hidden="true"></div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 border-2 border-white" aria-hidden="true"></div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-400 border-2 border-white" aria-hidden="true"></div>
                </div>
                <div>
                  <p className="text-gray-900 font-semibold" itemProp="userCount">10,000+ Athletes</p>
                  <p className="text-gray-600 text-sm">Training smarter already</p>
                </div>
              </div>
            </div>
            <div className="relative animate-float">
              <div className="relative z-10">
                <div className="bg-gradient-to-br from-purple-600 to-blue-500 rounded-3xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="bg-white rounded-2xl p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📸</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Photo Analysis</p>
                        <p className="text-sm text-gray-600">AI Processing...</p>
                      </div>
                    </div>
                    <div className="h-48 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center">
                      <span className="text-6xl">🏋️</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">✓ Exercise Identified</span>
                        <span className="text-xs text-green-600 font-semibold">Bench Press</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">3 Alternatives Found</span>
                        <span className="text-xs text-blue-600 font-semibold">View →</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-gradient-to-br from-purple-300 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-64 h-64 bg-gradient-to-br from-blue-300 to-indigo-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </header>

      {/* Core Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 id="features-heading" className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Revolutionary AI-Powered Fitness Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not just another fitness tracker. This is the future of personalized training.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <article className="group p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Photo snap icon">
                <span className="text-3xl" aria-hidden="true">📸</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Snap & Transform
              </h3>
              <p className="text-lg font-semibold text-purple-600 mb-3">
                AI-Powered Exercise Alternatives
              </p>
              <p className="text-gray-700 leading-relaxed">
                Take a picture of any exercise and instantly receive perfect alternatives tailored to your health goals, limitations, and equipment availability.
              </p>
            </article>

            {/* Feature 2 */}
            <article className="group p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Search icon">
                <span className="text-3xl" aria-hidden="true">🔍</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                1M+ Exercises
              </h3>
              <p className="text-lg font-semibold text-blue-600 mb-3">
                At Your Fingertips
              </p>
              <p className="text-gray-700 leading-relaxed">
                Search any exercise, see detailed muscle activation maps, difficulty ratings, and get instant alternatives that match your training style.
              </p>
            </article>

            {/* Feature 3 */}
            <article className="group p-8 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Target icon">
                <span className="text-3xl" aria-hidden="true">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Machine Scanning
              </h3>
              <p className="text-lg font-semibold text-indigo-600 mb-3">
                Scan Any Gym Equipment
              </p>
              <p className="text-gray-700 leading-relaxed">
                Quick access to exercise alternatives by scanning machine labels or browsing equipment lists. Know exactly what you can do with any machine.
              </p>
            </article>

            {/* Feature 4 */}
            <article className="group p-8 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Analytics icon">
                <span className="text-3xl" aria-hidden="true">📊</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Daily Activity Plan
              </h3>
              <p className="text-lg font-semibold text-cyan-600 mb-3">
                Track Progress Automatically
              </p>
              <p className="text-gray-700 leading-relaxed">
                See which muscles you've trained, track workout history, and get AI-adjusted recommendations based on your recovery and performance.
              </p>
            </article>

            {/* Feature 5 */}
            <article className="group p-8 bg-gradient-to-br from-violet-50 to-violet-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-violet-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Writing icon">
                <span className="text-3xl" aria-hidden="true">✍️</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Simple Manual Logging
              </h3>
              <p className="text-lg font-semibold text-violet-600 mb-3">
                Old School Meets New School
              </p>
              <p className="text-gray-700 leading-relaxed">
                Type your workouts note-style for maximum flexibility and customization. Perfect for tracking unique movements and personal variations.
              </p>
            </article>

            {/* Feature 6 - Bonus */}
            <article className="group p-8 bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-600 to-pink-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Apple Health icon">
                <span className="text-3xl" aria-hidden="true">🍎</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Apple Health Sync
              </h3>
              <p className="text-lg font-semibold text-pink-600 mb-3">
                Seamless Integration
              </p>
              <p className="text-gray-700 leading-relaxed">
                Syncs seamlessly with Apple Health to track all your metrics in one place. Your complete health picture, powered by AI.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Facing These Challenges?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">💪</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">Injured or limited mobility?</p>
                    <p className="text-gray-600">Can't do your usual exercises</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🏋️</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">Limited equipment?</p>
                    <p className="text-gray-600">Home gym or overcrowded facility</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">😴</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">Bored with your routine?</p>
                    <p className="text-gray-600">Same exercises every workout</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">❓</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">Not sure what to do?</p>
                    <p className="text-gray-600">Confused about exercise alternatives</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-2xl p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                  Fit AI Has You Covered
                </span>
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our AI analyzes your situation and provides instant alternatives that keep you on track—no matter what obstacles you face.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-xl">✓</span>
                  <span className="text-gray-900 font-medium">Personalized to YOUR body & goals</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-xl">✓</span>
                  <span className="text-gray-900 font-medium">Works with ANY equipment</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-xl">✓</span>
                  <span className="text-gray-900 font-medium">Adapts to your limitations</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-xl">✓</span>
                  <span className="text-gray-900 font-medium">Keeps training fresh & engaging</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three simple steps to transform your training
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-purple-400 rounded-full text-white text-3xl font-bold mb-6 shadow-lg">
                  1
                </div>
                <div className="h-64 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-7xl">📸</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Snap or Search
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Take a photo of any exercise or search our massive database. You can even scan gym equipment labels.
                </p>
              </div>
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-purple-400 to-blue-400 transform translate-x-0">
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full text-white text-3xl font-bold mb-6 shadow-lg">
                  2
                </div>
                <div className="h-64 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-7xl">🤖</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  AI Analysis
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our AI instantly analyzes your goals, limitations, and available equipment to find perfect alternatives.
                </p>
              </div>
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-blue-400 to-indigo-400 transform translate-x-0">
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                  <div className="w-3 h-3 bg-indigo-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-full text-white text-3xl font-bold mb-6 shadow-lg">
                  3
                </div>
                <div className="h-64 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-7xl">🚀</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Train Smarter
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Get personalized alternatives and track your progress. AI optimizes your training over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unique Selling Points Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Fit AI is Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Features you won't find anywhere else
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* USP 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">👥</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Community-Powered Library
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Add your own exercises—boxing, calisthenics, specialized movements. Help grow the most comprehensive exercise database and discover unique movements from athletes worldwide.
                  </p>
                </div>
              </div>
            </div>

            {/* USP 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🌍</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Works Everywhere
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Gym, home, park, hotel room—get intelligent alternatives anywhere. Limited equipment? No problem. Our AI adapts to whatever you have available.
                  </p>
                </div>
              </div>
            </div>

            {/* USP 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🧠</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Exercise Memory
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    AI remembers your training patterns to prevent overtraining. It learns from every workout and adjusts recommendations for optimal recovery and growth.
                  </p>
                </div>
              </div>
            </div>

            {/* USP 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🔒</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Privacy-Focused
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Your data stays yours. End-to-end encryption keeps your training data secure. We're committed to protecting your privacy while delivering powerful AI features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Features 
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Coming Soon
            </h2>
            <p className="text-xl text-gray-300">
              Exciting features in our roadmap
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">🎁</span>
              <h3 className="text-lg font-bold mb-2">Trade Promotions</h3>
              <p className="text-gray-400 text-sm">Exclusive gym & brand discounts for members</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">🍽️</span>
              <h3 className="text-lg font-bold mb-2">Nutrition Tracking</h3>
              <p className="text-gray-400 text-sm">Calories tracking & fridge scanner</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">📋</span>
              <h3 className="text-lg font-bold mb-2">Hybrid Training Plans</h3>
              <p className="text-gray-400 text-sm">AI-generated custom workout programs</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">🌡️</span>
              <h3 className="text-lg font-bold mb-2">Pain Thermometer</h3>
              <p className="text-gray-400 text-sm">Daily pain tracking and exercise adjustments</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">💰</span>
              <h3 className="text-lg font-bold mb-2">Cash Giveaways</h3>
              <p className="text-gray-400 text-sm">Monthly rewards for active members</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <span className="text-3xl mb-3 block">🤝</span>
              <h3 className="text-lg font-bold mb-2">Social Features</h3>
              <p className="text-gray-400 text-sm">Connect with training partners & coaches</p>
            </div>
          </div>
        </div>
      </section>*/}

      {/* CTA / Waitlist Section */}
      <section id="waitlist" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Start Training Smarter Today
          </h2>
          <p className="text-xl sm:text-2xl text-purple-100 mb-8">
            Join thousands of athletes who are revolutionizing their training with AI
          </p>
          
          {!isSubmitted ? (
            <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-6 py-4 rounded-full text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-purple-300"
                  aria-label="Email address"
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-purple-600 rounded-full font-bold text-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-xl"
                  aria-label="Join Waitlist"
                  tabIndex={0}
                >
                  Join Waitlist
                </button>
              </div>
            </form>
          ) : (
            <div className="max-w-md mx-auto bg-white/20 backdrop-blur-lg rounded-2xl p-6 animate-slide-up">
              <p className="text-white text-xl font-semibold">
                🎉 You're on the list! Check your email soon.
              </p>
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">📱</span>
              <span className="text-lg">Available on iOS & Android</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🔒</span>
              <span className="text-lg">Privacy-Focused</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🚀</span>
              <span className="text-lg">Launching Soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8" role="contentinfo">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl flex items-center justify-center" role="img" aria-label="Fit AI Logo">
                  <span className="text-white font-bold text-xl">F</span>
                </div>
                <span className="text-2xl font-bold text-white">Fit AI</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Your AI-Powered Fitness Companion. Built for athletes, by athletes.
              </p>
              <nav className="flex space-x-4" aria-label="Social media links">
                <a
                  href="https://twitter.com/FitAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors duration-200"
                  aria-label="Follow Fit AI on Twitter"
                  tabIndex={0}
                >
                  <span className="text-xl" aria-hidden="true">𝕏</span>
                </a>
                <a
                  href="https://instagram.com/FitAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors duration-200"
                  aria-label="Follow Fit AI on Instagram"
                  tabIndex={0}
                >
                  <span className="text-xl" aria-hidden="true">📷</span>
                </a>
                <a
                  href="https://youtube.com/@FitAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors duration-200"
                  aria-label="Subscribe to Fit AI on YouTube"
                  tabIndex={0}
                >
                  <span className="text-xl" aria-hidden="true">▶️</span>
                </a>
                <a
                  href="https://linkedin.com/company/fitai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors duration-200"
                  aria-label="Follow Fit AI on LinkedIn"
                  tabIndex={0}
                >
                  <span className="text-xl" aria-hidden="true">💼</span>
                </a>
              </nav>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Features</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>How It Works</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Pricing</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Roadmap</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>About Us</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Blog</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Careers</button></li>
                <li><button className="hover:text-purple-400 transition-colors" tabIndex={0}>Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 sm:mb-0">
              © 2025 Fit AI. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <button className="hover:text-purple-400 transition-colors" tabIndex={0}>Privacy Policy</button>
              <button className="hover:text-purple-400 transition-colors" tabIndex={0}>Terms of Service</button>
              <button className="hover:text-purple-400 transition-colors" tabIndex={0}>Cookie Policy</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
